import { useEffect, useState } from 'react';
import { getObjectWithSelector } from '../../../functions/objectFunctions/getObjectWithSelector';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { parseBoxShadowString } from '../../../functions/utils/parseBoxShadowString';
import { useElements, useScene } from '../../../hooks';
import { LauncherElement } from '../../../types/launcherElement';
import { ColorInput } from '../inputs/ColorInput';
import { UnitInput } from '../inputs/UnitInput';

export const BoxShadowInputGroup = ({
  variant,
  tab,
  pseudoclass,

  colorPickerExtended,
  setColorPickerExtended,
}: {
  variant?: string;
  tab?: string;
  pseudoclass?: string;

  colorPickerExtended?: boolean;
  setColorPickerExtended?: (newValue: boolean) => void;
}) => {
  const scene = useScene();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [selectedElement, setSelectedElement] = useElements<LauncherElement>({
    id: lastSelectedElementID,
  });

  let selector: string = 'props.class';
  if (variant && tab) {
    selector = `props.class.variants.${variant}.${tab}`;
  }

  if (pseudoclass === 'none') pseudoclass = undefined;
  if (pseudoclass) {
    selector = `${selector}.&:${pseudoclass}`;
  }

  const styleObj = getObjectWithSelector(selectedElement, selector);

  const labelWidth = 100;

  const [shadow, setShadow] = useState(
    parseBoxShadowString(styleObj?.boxShadow)
  );

  useEffect(() => {
    setShadow(parseBoxShadowString(styleObj?.boxShadow));
  }, [lastSelectedElementID]);

  useEffect(() => {
    if (
      (shadow.blur === null || shadow.blur === '0px') &&
      (shadow.spread === null || shadow.spread === '0px') &&
      (shadow.color === null || shadow.color === '#000000')
    ) {
      return;
    }

    let boxShadowString: string | undefined;

    boxShadowString = `${shadow.offsetX || '0px'} ${shadow.offsetY || '0px'} ${
      shadow.blur || '0px'
    } ${shadow.spread || '0px'} ${shadow.color || '#000000'}`;

    if (!shadow.blur && !shadow.spread && !shadow.color)
      boxShadowString = undefined;

    setSelectedElement((prev) =>
      updateObjectNestedProperties(prev, {
        [`${selector}.boxShadow`]: boxShadowString,
      })
    );
  }, [shadow]);

  const updateShadow = (
    property: 'offsetX' | 'offsetY' | 'blur' | 'spread' | 'color',
    newValue: string | undefined
  ) => {
    setShadow((prev) => ({ ...prev, [property]: newValue }));
  };

  return (
    <>
      <UnitInput
        id={`box-shadow-spread-input`}
        label="Shadow Spread"
        value={shadow.spread || undefined}
        onChange={(inputValue) =>
          updateShadow('spread', inputValue ? String(inputValue) : '')
        }
        defaultUnit="px"
        units={['px']}
        labelWidth={labelWidth}
      />
      <UnitInput
        id={`box-shadow-blur-input`}
        label="Shadow Blur"
        value={shadow.blur || undefined}
        onChange={(inputValue) =>
          updateShadow('blur', inputValue ? String(inputValue) : '')
        }
        defaultUnit="px"
        units={['px']}
        labelWidth={labelWidth}
      />
      <ColorInput
        id={`box-shadow-color-input`}
        label="Shadow Color"
        value={shadow.color || undefined}
        onChange={(inputValue) => updateShadow('color', inputValue)}
        extended={colorPickerExtended}
        setExtended={setColorPickerExtended}
        labelWidth={labelWidth}
      />
    </>
  );
};
